'use client';

import React, { useCallback } from 'react';

import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';

import { useTrackPublisherEvents } from '@/api/useTrackPublisherEvents';
import {
  handleBlockClick,
  isComponentBlock,
  shouldCenterBlockItems,
  shouldUseColumnLayout,
} from '@/lib/blockUtils';
import { BlockType, IBlock, IBlockItem } from '@/lib/types';
import { retryChunkImport } from '@/lib/retryChunkImport';
import { cn } from '@/lib/utils';
import { useAppState } from '@/state/hooks/app.state.hook';

import { CtaItem } from '../BlockItems/Cta';
import { DailyBonusItem } from '../BlockItems/DailyBonus';
import { HtmlItem } from '../BlockItems/Html';
import { OfferItem } from '../BlockItems/Offer';
import { IframeItem } from '../BlockItems/Iframe';
import { MediaItem } from '../BlockItems/Media/Media';
import { PostItem } from '../BlockItems/Post';
import { BlockTitle } from '../BlockItems/Title';

// The progress bar is shared-ui's offer widget and brings framer-motion and
// shared-ui's own lodash and device-detect copies with it (~115KB gzipped),
// which most pages never render. It renders nothing until a logged-in player's
// offers arrive, so the client-only render matches the server's empty output.
const ProgressBarItem = dynamic(
  () =>
    retryChunkImport(() =>
      import('../BlockItems/ProgressBar').then((mod) => mod.ProgressBarItem)
    ),
  { ssr: false }
);

const MAP_ITEM_TO_COMPONENT: Record<BlockType, React.ComponentType<any>> = {
  image: MediaItem,
  video: MediaItem,
  news: PostItem,
  article: PostItem,
  contentsPreview: PostItem,
  cta: CtaItem,
  iframe: IframeItem,
  dailyBonus: DailyBonusItem,
  progressBar: ProgressBarItem,
  html: HtmlItem,
  offer: OfferItem,
};

interface IBlockProps extends IBlock {
  blockIndex: string;
  blockType: BlockType;
  /** The first row of the page's primary post block: its first card loads eagerly. */
  isPrimaryPostBlock?: boolean;
  /** Below the primary post block: HTML block images are lazy-loaded. */
  isBelowPrimaryPostBlock?: boolean;
}

export const BlockRow: React.FC<IBlockProps> = ({
  title,
  items,
  blockIndex,
  isPrimaryPostBlock = false,
  isBelowPrimaryPostBlock = false,
  type: blockType,
  contentType,
  redirectUrl,
  html,
  htmlSanitized,
  offerIds,
  offerType,
}) => {
  const { trackPublisherUIEvent } = useTrackPublisherEvents();
  const { platform, currentPageName, playerData } = useAppState();
  const pathname = usePathname();

  const handleBlockClickCallback = useCallback(
    (item: IBlockItem) => {
      return handleBlockClick({
        item,
        blockType,
        pathname,
        currentPageName,
        platform,
        playerId: playerData?.playerId,
        trackPublisherUIEvent,
      });
    },
    [blockType, pathname, currentPageName, platform, playerData?.playerId]
  );

  const renderComponent = (componentType: BlockType, item?: IBlockItem, itemIndex = 0) => {
    const Component = MAP_ITEM_TO_COMPONENT[componentType];
    if (!Component) return null;

    const uniqueIdentifier = `${blockIndex}-${itemIndex}`;
    const props = item || {
      type: componentType,
      redirectUrl,
      html,
      htmlSanitized,
      offerIds,
      offerType,
    };
    const postProps =
      Component === PostItem ? { priority: isPrimaryPostBlock && itemIndex === 0 } : {};
    const htmlProps = Component === HtmlItem ? { lazyImages: isBelowPrimaryPostBlock } : {};

    return (
      <Component
        key={uniqueIdentifier}
        {...props}
        {...postProps}
        {...htmlProps}
        uniqueIdentifier={uniqueIdentifier}
        itemIndex={itemIndex}
        type={componentType}
        onBlockClick={() =>
          handleBlockClickCallback(item || ({ type: componentType } as IBlockItem))
        }
      />
    );
  };

  return (
    <div
      data-testid="PortalBlock-Container"
      className={cn('blockWrapper flex w-full flex-col gap-5 md:gap-[22px]')}
    >
      {title && <BlockTitle title={title} />}
      <div
        className={cn(
          'blockItems flex w-full',
          !isComponentBlock(blockType) && 'gap-5 md:gap-[22px]',
          shouldUseColumnLayout(blockType) ? 'flex-col' : 'flex-wrap',
          shouldCenterBlockItems(blockType) && 'justify-center'
        )}
      >
        {isComponentBlock(blockType)
          ? renderComponent(blockType)
          : items?.map((item: IBlockItem, itemIndex: number) => {
              const itemType = item.type || contentType || blockType;

              return renderComponent(itemType, item, itemIndex);
            })}
      </div>
    </div>
  );
};
